import React from "react";
import { Link } from "react-router-dom";
import Icon from "./Icons";

const features = [
  {
    icon: "library",
    number: "01",
    title: "A library that remembers",
    body: "Track every title, reading status, rating, and note without turning reading into admin.",
  },
  {
    icon: "sparkle",
    number: "02",
    title: "Recommendations with taste",
    body: "Personal picks shaped by your actual reading history, not a generic bestseller list.",
  },
  {
    icon: "chat",
    number: "03",
    title: "Stories that keep talking",
    body: "Step back into a finished book and have a thoughtful conversation with its characters.",
  },
];

const miniBooks = [
  { title: "The Left Hand of Darkness", tone: "from-[#283c49] to-[#b36a4f]", progress: 68 },
  { title: "Pachinko", tone: "from-[#5a2738] to-[#c68064]", progress: 34 },
  { title: "Sea of Tranquility", tone: "from-[#163b4b] to-[#4b8190]", progress: 82 },
];

function ProductPreview() {
  return (
    <div className="relative mx-auto w-full max-w-5xl">
      <div className="absolute -left-8 top-16 h-32 w-32 rounded-full bg-gold/20 blur-3xl" />
      <div className="absolute -right-10 bottom-10 h-44 w-44 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-ink-surface/90 p-2 shadow-[0_40px_120px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:p-3">
        <div className="overflow-hidden rounded-[22px] border border-ink-border bg-[#0d141c]">
          <div className="flex items-center justify-between border-b border-ink-border px-4 py-3 sm:px-6">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff7961]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#f5bd58]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#5cc18a]" />
            </div>
            <div className="hidden items-center gap-5 text-[10px] text-cream-muted sm:flex">
              <span className="text-cream">Today</span><span>Library</span><span>Journal</span><span>Collections</span>
            </div>
            <div className="h-7 w-7 rounded-full border border-ink-border bg-ink-raised" />
          </div>

          <div className="grid min-h-[390px] gap-4 p-4 sm:grid-cols-[1.45fr_0.75fr] sm:p-6 md:gap-6 md:p-8">
            <div>
              <p className="sc-kicker mb-2">Tuesday, July 14</p>
              <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                  <h3 className="font-serif text-3xl text-cream sm:text-4xl">Good evening, Asif.</h3>
                  <p className="mt-1 text-xs text-cream-muted">Pick up where you left off.</p>
                </div>
                <span className="hidden rounded-xl bg-gold px-3 py-2 text-[10px] font-semibold text-ink-base sm:block">+ Add book</span>
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {miniBooks.map((book, index) => (
                  <div key={book.title} className={`transition-transform duration-500 hover:-translate-y-2 ${index === 1 ? "translate-y-3" : ""}`}>
                    <div className={`relative aspect-[2/3] overflow-hidden rounded-xl bg-gradient-to-br ${book.tone} p-3 shadow-card`}>
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_42%)]" />
                      <p className="relative mt-3 font-serif text-sm font-semibold leading-tight text-white sm:text-base">{book.title}</p>
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="h-1 overflow-hidden rounded-full bg-black/30">
                          <div className="h-full rounded-full bg-white/80" style={{ width: `${book.progress}%` }} />
                        </div>
                        <p className="mt-1 text-[8px] text-white/70">{book.progress}% read</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="rounded-2xl border border-ink-border bg-ink-surface p-4">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.16em] text-cream-muted">Yearly goal</p>
                    <p className="font-serif text-2xl text-cream">18 <span className="text-sm text-cream-muted">/ 24</span></p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border-4 border-gold text-[9px] font-bold text-gold">75%</div>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-ink-raised"><div className="h-full w-3/4 rounded-full bg-gold" /></div>
              </div>

              <div className="flex-1 rounded-2xl border border-ink-border bg-gradient-to-br from-ink-surface to-[#16252b] p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold/15 text-gold"><Icon name="sparkle" size={17} /></div>
                <p className="mt-4 text-[9px] uppercase tracking-[0.16em] text-gold">Picked for you</p>
                <p className="mt-1 font-serif text-xl leading-tight text-cream">The Memory Police</p>
                <p className="mt-1 text-[10px] text-cream-muted">Yoko Ogawa</p>
                <p className="mt-3 text-[10px] leading-relaxed text-cream-dim">A quiet, haunting novel about memory, loss, and the things we hold onto.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-semibold text-gold">See why it fits <Icon name="arrow" size={12} /></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <div className="overflow-hidden bg-ink-base">
      <section className="relative mx-auto max-w-7xl px-5 pb-20 pt-20 text-center sm:px-8 sm:pb-28 sm:pt-28">
        <div className="pointer-events-none absolute left-1/2 top-24 h-80 w-80 -translate-x-1/2 rounded-full bg-gold/10 blur-[110px]" />
        <div className="relative mx-auto max-w-4xl">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-gold/20 bg-gold/[0.07] px-3 py-1.5 text-[11px] font-medium text-gold">
            <Icon name="sparkle" size={14} /> Your books, beautifully remembered
          </div>
          <h1 className="font-serif text-5xl font-semibold leading-[0.95] tracking-[-0.045em] text-cream sm:text-7xl md:text-[92px]">
            A richer life<br />between the <span className="italic text-gold">pages.</span>
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-cream-dim sm:text-lg">
            StoryChats is a calm, intelligent home for everything you read—what moved you, what comes next, and the characters you are not ready to leave.
          </p>
          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/register" className="sc-btn-gold px-7 py-3.5 no-underline">
              Build your library <Icon name="arrow" size={16} />
            </Link>
            <Link to="/login" className="sc-btn-ghost px-7 py-3.5 no-underline">I already have one</Link>
          </div>
          <p className="mt-4 text-[11px] text-cream-muted">Free to start · No credit card · Takes 30 seconds</p>
        </div>
      </section>

      <section className="px-3 pb-28 sm:px-8 sm:pb-36">
        <ProductPreview />
      </section>

      <section className="border-y border-white/[0.06] bg-white/[0.015]">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <div className="mb-16 grid gap-6 md:grid-cols-[0.8fr_1.2fr] md:items-end">
            <p className="sc-kicker">Designed around readers</p>
            <h2 className="font-serif text-4xl leading-[1.05] tracking-[-0.03em] text-cream sm:text-5xl md:text-6xl">Less tracking.<br /><span className="text-cream-muted">More remembering.</span></h2>
          </div>

          <div className="grid gap-px overflow-hidden rounded-3xl border border-ink-border bg-ink-border md:grid-cols-3">
            {features.map(feature => (
              <article key={feature.title} className="group bg-ink-surface p-7 transition-colors duration-300 hover:bg-ink-raised sm:p-9">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/10 text-gold transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110"><Icon name={feature.icon} size={20} /></span>
                  <span className="font-serif text-sm text-cream-muted">{feature.number}</span>
                </div>
                <h3 className="mt-10 font-serif text-2xl text-cream">{feature.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream-muted">{feature.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="relative overflow-hidden rounded-[32px] border border-gold/20 bg-gradient-to-br from-[#17242d] via-ink-surface to-[#211c1b] px-6 py-16 text-center shadow-lift sm:px-12 sm:py-20">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,138,101,0.16),transparent_45%)]" />
          <div className="relative">
            <p className="sc-kicker">Your next chapter</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-serif text-4xl leading-tight tracking-[-0.03em] text-cream sm:text-6xl">Your reading life deserves a beautiful home.</h2>
            <Link to="/register" className="sc-btn-gold mt-8 px-7 py-3.5 no-underline">Start for free <Icon name="arrow" size={16} /></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/[0.06]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-7 text-xs text-cream-muted sm:px-8">
          <span className="font-serif text-sm text-cream-dim">StoryChats</span>
          <span>Made for people who keep thinking after the last page.</span>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
